'use client'

import { Box, Flex, Stack, Table, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'

import type { Contract, CryptoEvent } from '@/services/coin/predictions'

import styles from '../styles.module.css'
import { useDateBadge } from './useDateBadge'

function parseChance(chance?: string): string {
  if (!chance) return '-'
  const num = parseFloat(chance)
  if (num > 99) return '>99'
  if (num < 1) return '<1'
  return `${Math.floor(num)}`
}

const badgeLabelMap = {
  live: 'badge.live',
  today: 'badge.today',
  tomorrow: 'badge.tomorrow',
  resolved: 'badge.resolved',
  inDays: 'badge.inDays',
} as const

export default function PredictionCard({ event }: { event: CryptoEvent }) {
  const t = useTranslations('trending.prediction')
  const dateBadge = useDateBadge(event.close_date, event.status)

  return (
    <a
      href={`https://web.crypto.com/explore/predict/events/details/${event.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.predictionLink}
    >
      <Stack className={styles.predictionCard}>
        <Box
          className={styles.predictionHeader}
          style={{ backgroundImage: `url('${event.web_landing_img}')` }}
        >
          {dateBadge && (
            <Box
              className={styles.predictionBadge}
              style={{ background: 'rgba(17, 153, 250, 0.20)' }}
            >
              <Text variant="micro-text" fw={500} c="#92D1FF">
                {t(badgeLabelMap[dateBadge.key], dateBadge.values)}
              </Text>
            </Box>
          )}
          <Text fz={16} fw={600} lh="24px" style={{ marginTop: 'auto' }}>
            {event.title}
          </Text>
        </Box>
        <Box className={styles.predictionContractList}>
          <Table withRowBorders={false} horizontalSpacing={0} verticalSpacing={10}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Text variant="micro-text" fw={500} c="#A0A9BE">
                    {t('selection')}
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: '1%' }}>
                  <Text variant="micro-text" fw={500} c="#A0A9BE">
                    {t('chanceToWin')}
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {event.contracts?.map((contract: Contract) => (
                <Table.Tr key={contract.id}>
                  <Table.Td>
                    <Text variant="body-number" fw={500}>
                      {contract.contract_title}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Flex align="center" gap={16}>
                      <Text variant="body-number" miw={40}>
                        <Box component="span" className={styles.predictionChance}>
                          {parseChance(contract.chance)}
                        </Box>
                        %
                      </Text>
                      <Flex gap={8}>
                        <Box className={styles.predictionCtaYes}>{t('yes')}</Box>
                        <Box className={styles.predictionCtaNo}>{t('no')}</Box>
                      </Flex>
                    </Flex>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </a>
  )
}
